import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import dayjs from 'dayjs';
import {
  Door,
  DoorBatteryWarningLevel,
  Mutation,
  MutationDeleteDoorByIdArgs,
  MutationUpdateDoorArgs,
  Query
} from 'graphql/generated/graphqlTypes';
import { DELETE_DOOR_BY_ID, UPDATE_DOOR_WITH_OFFICE_MODE } from 'graphql/mutations';
import { GET_DOORS_FOR_TENANT_WITH_OFFICE_MODE } from 'graphql/queries';
import { useEffect } from 'react';
import { deleteDoorsAction, setDoorsAction, updateDoorsAction } from 'state/actions/doors';
import { doorsVar } from 'state/vars';
import ArrayUtil from 'utils/Array/Array.util';
import { BaseHookProps } from '../shared/types';

const useDoors = ({ handleFetchError }: BaseHookProps) => {
  const doors = useReactiveVar(doorsVar);

  const [getDoorsForTenant, { loading }] = useLazyQuery<Pick<Query, 'getDoorsForTenant'>>(
    GET_DOORS_FOR_TENANT_WITH_OFFICE_MODE,
    {
      onCompleted: (data) => {
        setDoorsAction(
          data.getDoorsForTenant.map((door) =>
            // we need to display 'powered' battery status for hi-sec reader
            door.hardwareIdentifier.startsWith('65') || door.hardwareIdentifier.startsWith('66')
              ? { ...door, batteryWarningLevel: DoorBatteryWarningLevel.Powered }
              : door
          )
        );
      },
      onError: () => {
        handleFetchError('Error while fetching doors');
      }
    }
  );

  const [updateDoorWithOfficeModeMutation, { loading: updateDoorWithOfficeModeLoading }] = useMutation<
    Pick<Mutation, 'updateDoor'>,
    MutationUpdateDoorArgs
  >(UPDATE_DOOR_WITH_OFFICE_MODE, {
    onCompleted: (data) => {
      updateDoorsAction(data.updateDoor);
    },
    onError: () => {
      handleFetchError('Error while updating doors');
    }
  });

  const [deleteDoorMutation, { loading: deleteDoorLoading }] = useMutation<
    Pick<Mutation, 'deleteDoorById'>,
    MutationDeleteDoorByIdArgs
  >(DELETE_DOOR_BY_ID, {
    onCompleted: (data) => {
      if (data.deleteDoorById) deleteDoorsAction(data.deleteDoorById);
    },
    onError: () => {
      handleFetchError('Error while deleting doors');
    }
  });

  const updateDoorWithOfficeMode = async (
    doorId: string,
    name: string,
    locationId?: string | null,
    officeModeFrom?: string,
    officeModeTo?: string
  ) => {
    await updateDoorWithOfficeModeMutation({
      variables: {
        doorId,
        name,
        locationId,
        officeMode: {
          scheduleType: 'weekSchedule',
          weekSchedule:
            officeModeFrom && officeModeTo
              ? // eslint-disable-next-line no-magic-numbers
                ArrayUtil.Range(7).map((_, index) => ({
                  dayName: dayjs().weekday(index).format('dddd').toLowerCase(),
                  from: officeModeFrom,
                  to: officeModeTo
                }))
              : []
        }
      },
      // Here Apollo doesn't update cache automatically, we have to do it manually
      update(cache, { data: updatedDoors }) {
        cache.writeQuery({
          query: GET_DOORS_FOR_TENANT_WITH_OFFICE_MODE,
          data: {
            getDoorsForTenant: doors.map((door) => (door.id === updatedDoors?.updateDoor.id ? updatedDoors : door))
          }
        });
      }
    });
  };

  const deleteDoor = async (doorId: string) => {
    await deleteDoorMutation({
      variables: {
        doorId
      },
      // Here Apollo doesn't update cache automatically, we have to do it manually
      update(cache, { data: deletedDoor }) {
        const updatedDoors = doors.filter(({ id }) => id !== deletedDoor?.deleteDoorById);
        cache.writeQuery({
          query: GET_DOORS_FOR_TENANT_WITH_OFFICE_MODE,
          data: {
            getDoorsForTenant: updatedDoors
          }
        });
      }
    });
  };

  const isOfficeMode = (door: Door) => door.officeMode.weekDays.some((day) => day !== null);

  useEffect(() => {
    getDoorsForTenant();
  }, [getDoorsForTenant]);

  return {
    doors,
    updateDoorWithOfficeMode,
    updateDoorWithOfficeModeLoading,
    deleteDoor,
    deleteDoorLoading,
    loading,
    isOfficeMode
  };
};

export default useDoors;
